import { useMemo, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams } from 'modules/i18n';
import {
  signalCalendarBar,
  signalDayCell,
  signalMono,
  signalPagingArrow
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  hasSelectableMonth,
  initialMonth,
  monthGrid,
  parseIso,
  shiftMonth
} from './dateCalendar';
import { readDatePickerAnswer, readDatePickerConfig } from './datePicker';
import { BlockRendererProps, questionIdOf } from './types';

/**
 * `date_picker@1`: one calendar date.
 *
 * A calendar drawn in this vocabulary rather than the OS picker. The native control is correct and
 * looks like nothing else in the form — it is the system's chrome dropped into the middle of a
 * designed flow, and on desktop it is a different control again on every browser.
 *
 * What the native input was right about is kept, and it is the important part: **the answer is never
 * a `Date`.** pro-core stores a plain calendar date with no time and no zone, precisely so "I can
 * start on the 3rd" cannot drift to the 2nd for someone in another offset. Every value here is a
 * `YYYY-MM-DD` string and the window is checked by string comparison; `dateCalendar.ts` holds the
 * arithmetic and never parses a string into a `Date`.
 *
 * Days outside the allowed window are disabled rather than hidden. A gap where the 14th should be
 * reads as a rendering fault; a dimmed 14th reads as a rule, which is what it is.
 */
export const DatePickerBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readDatePickerConfig(block), [block]);
  const value = readDatePickerAnswer(answer)?.date ?? null;

  // Opens on the answer, or on the start of the window — never on "today", which for a window that
  // begins next month would open on a page with nothing to choose.
  const [month, setMonth] = useState(() =>
    initialMonth(value, config.minDate, new Date())
  );

  const questionId = questionIdOf(block);

  const cells = useMemo(
    () => monthGrid(month, config.minDate, config.maxDate),
    [month, config.minDate, config.maxDate]
  );

  const previous = shiftMonth(month, -1);
  const next = shiftMonth(month, 1);
  const canGoBack = hasSelectableMonth(
    previous,
    config.minDate,
    config.maxDate
  );
  const canGoForward = hasSelectableMonth(next, config.minDate, config.maxDate);

  const monthLabel = new Date(
    month.year,
    month.month - 1,
    1
  ).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  // Weekday initials from the locale rather than a hardcoded list. 2024-01-01 was a Monday, which is
  // the column order the grid is built for.
  const weekdays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) =>
        new Date(2024, 0, 1 + index).toLocaleDateString(locale, {
          weekday: 'short'
        })
      ),
    [locale]
  );

  const windowText = (): string => {
    const { minDate, maxDate } = config;
    if (minDate && maxDate) {
      return getTransWithParams('guestForm.date.between', {
        from: formatIso(minDate, locale),
        to: formatIso(maxDate, locale)
      });
    }
    if (minDate) {
      return getTransWithParams('guestForm.date.from', {
        from: formatIso(minDate, locale)
      });
    }
    if (maxDate) {
      return getTransWithParams('guestForm.date.until', {
        to: formatIso(maxDate, locale)
      });
    }
    return '';
  };

  return (
    <Stack spacing={3}>
      {/* No derived hint: the calendar states its own window underneath, where it sits beside the
          days it is dimming. Saying it twice would train people to skip the line that varies. */}
      <BlockHeading block={block} locale={locale} />

      <Box>
        <Box sx={signalCalendarBar}>
          {/* A live region: paging replaces the whole grid, and a screen-reader user has to be told
              which month they landed on. */}
          <Typography aria-live="polite" sx={{ fontSize: 15, fontWeight: 500 }}>
            {monthLabel}
          </Typography>

          {/* Both arrows together, as in the sample. Paging is one job done twice, and a thumb that
              has just pressed back is already over forward — split across the width, every second
              press is a journey. */}
          <Stack direction="row" spacing={0.75}>
            <ButtonBase
              onClick={() => setMonth(previous)}
              disabled={!canGoBack}
              aria-label={getTrans('guestForm.date.previousMonth')}
              data-testid="form-date-prev"
              sx={signalPagingArrow}
            >
              <ChevronLeftIcon sx={{ fontSize: 15 }} aria-hidden="true" />
            </ButtonBase>
            <ButtonBase
              onClick={() => setMonth(next)}
              disabled={!canGoForward}
              aria-label={getTrans('guestForm.date.nextMonth')}
              data-testid="form-date-next"
              sx={signalPagingArrow}
            >
              <ChevronRightIcon sx={{ fontSize: 15 }} aria-hidden="true" />
            </ButtonBase>
          </Stack>
        </Box>

        <Box
          role="grid"
          aria-labelledby={questionId}
          // The calendar replaced a native input that carried this, and nothing put it back — so a
          // required date question announced itself as optional, which is the one thing a required
          // question must not do.
          aria-required={block.required ? true : undefined}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px'
          }}
        >
          {weekdays.map(weekday => (
            <Typography
              key={weekday}
              role="columnheader"
              sx={{
                fontSize: 11,
                textAlign: 'center',
                color: 'text.disabled',
                pb: '6px'
              }}
            >
              {weekday}
            </Typography>
          ))}

          {cells.map(cell =>
            cell.iso === null ? (
              <Box key={cell.padKey} aria-hidden="true" />
            ) : (
              <ButtonBase
                key={cell.iso}
                role="gridcell"
                aria-selected={cell.iso === value}
                // The full date, not the number: "14" tells a screen-reader user nothing about
                // which month they are in.
                aria-label={formatIso(cell.iso, locale)}
                disabled={!cell.selectable}
                onClick={() => onChange({ date: cell.iso })}
                data-testid={`form-date-${cell.iso}`}
                sx={signalDayCell(cell.iso === value)}
              >
                {cell.day}
              </ButtonBase>
            )
          )}
        </Box>
      </Box>

      {windowText() && (
        <Typography
          sx={{ ...signalMono, fontSize: 12, color: 'text.disabled' }}
        >
          {windowText()}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * Show a date the way the respondent's locale writes them.
 *
 * Parsed by parts rather than by `new Date(iso)`, which reads a bare `YYYY-MM-DD` as UTC midnight
 * and can render the previous day for anyone west of it — the same off-by-one this whole block is
 * arranged to avoid, and careless to reintroduce in a label.
 */
const formatIso = (iso: string, locale: string): string => {
  const parts = parseIso(iso);
  if (!parts) {
    return iso;
  }
  return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString(
    locale,
    { day: '2-digit', month: '2-digit', year: 'numeric' }
  );
};
